import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	useMutation,
} from 'react-query';
import {
	Col,
	Row,
} from 'antd';
import {
	updatePortCostCategories,
} from '@client/lib/api';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import EditableTable from '@client/components/EditableTable/index';
import { EditableCell } from '@client/components/EditableTable/EditableCell';
import showErrorNotification from '@client/utils/showErrorNotification';
import { useAuth } from '@client/lib/auth';

const PortCostCategories = () => {
	const [localCategories, setLocalCategories] = useState<Array<{ id: string; name: string }>>([]);
	const { userInfo, refreshAuthInfo } = useAuth();

	useEffect(() => {
		const categories = userInfo.portCostCategories || [];
		setLocalCategories(categories.map((name, index) => ({ id: `category-${index}-${name.replace(/\s+/g, '-')}`, name })));
	}, [userInfo.portCostCategories]);

	const updateMutation = useMutation(
		(categories: string[]) => updatePortCostCategories(categories),
		{
			onSuccess: async () => {
				await refreshAuthInfo();
				showSuccessNotification('Port cost categories updated!');
			},
			onError: (e) => {
				showErrorNotification((e as unknown as Error).toString());
			},
		},
	);

	const onAdd = (values: { name: string }) => {
		const categoryNames = [...(userInfo.portCostCategories || []), values.name];
		updateMutation.mutate(categoryNames);
	};

	const onSave = async (id: string, attributes: { name: string }) => {
		const categoryIndex = localCategories.findIndex(category => category.id === id);

		if (categoryIndex === -1) {
			return;
		}
		
		const categoryNames = [...(userInfo.portCostCategories || [])];
		categoryNames[categoryIndex] = attributes.name;
		updateMutation.mutate(categoryNames);
	};

	const onDelete = (id: string) => {
		const categoryIndex = localCategories.findIndex(category => category.id === id);

		if (categoryIndex === -1) {
			return;
		}
		
		const categoryNames = [...(userInfo.portCostCategories || [])];
		categoryNames.splice(categoryIndex, 1);
		updateMutation.mutate(categoryNames);
	};

	const columns = useMemo(() => {
		return [
			{
				dataIndex: 'name',
				title: 'Category',
				editable: true,
				editingProps: {
					type: 'text',
				},
			},
		];
	}, []);

	return (
		<Row>
			<Col xxl={8} xl={10} lg={16} sm={18}>
				<EditableTable<{ id: string; name: string }, 'id'>
					size="small"
					allowAddNew
					addNewText="Add category"
					onAddNew={onAdd}
					onDelete={onDelete}
					// @ts-ignore
					columns={columns}
					iconButtons
					components={{
						body: {
							cell: EditableCell,
						},
					}}
					keyDataIndex="id"
					onSave={onSave}
					dataSource={localCategories}
					pagination={false}
					loading={updateMutation.isLoading}
					enableDelete
				/>
			</Col>
		</Row>
	);
};

export default PortCostCategories;
